import { initializeApp } from 'firebase/app';
import { getFirestore, initializeFirestore, persistentLocalCache, persistentSingleTabManager, getPersistentCacheIndexManager, enablePersistentCacheIndexAutoCreation, clearIndexedDbPersistence } from 'firebase/firestore';
import { initializeAuth, browserLocalPersistence, indexedDBLocalPersistence } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import { getInstallations } from 'firebase/installations';
import { getMessaging, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: "AIzaSyA7MiLqUGmtI25eZCVMZdNno3LxzFZvKfU",
  authDomain: "rsmaidsbooking.firebaseapp.com",
  projectId: "rsmaidsbooking",
  storageBucket: "rsmaidsbooking.firebasestorage.app",
  messagingSenderId: "286713574927",
  appId: "1:286713574927:web:7045f1d68c9c4f17fc157a",
  measurementId: "G-Y44MRN2FL5"
};

const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);
export const dbRsRoles = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentSingleTabManager(undefined) })
}, 'rs-roles');
export const dbContacts = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentSingleTabManager(undefined) })
}, 'contacts');
// Customer contacts must not stay on the device once the person has signed out. Firestore only lets its saved copy be cleared
// before the database has been used, so sign-out leaves this flag (see clearContactsCache) and the copy is removed here at the
// next start, ahead of anything that reads the database.
try {
  if (localStorage.getItem('rs.clearContactsCopy')) {
    clearIndexedDbPersistence(dbContacts)
      .then(() => localStorage.removeItem('rs.clearContactsCopy'))
      .catch((error) => console.warn('[firebase] Could not clear the saved contacts copy:', error));
  }
} catch { /* storage blocked: nothing was flagged */ }
export const dbBookings = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentSingleTabManager(undefined) })
}, 'rsmaidsbooking');
// Without local indexes, every cache query ("startDate in range") scans ALL
// cached docs in the collection — fine on desktop, very slow in the Android
// WebView's IndexedDB. Let the SDK build indexes for the queries we run.
const bookingsIndexManager = getPersistentCacheIndexManager(dbBookings);
if (bookingsIndexManager) enablePersistentCacheIndexAutoCreation(bookingsIndexManager);
// Texts and chat messages: persistent local cache like the databases above. With only the default in-memory
// cache every thread open went to the server (~1s), whatever had been read a moment ago.
export const dbTwilio = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentSingleTabManager(undefined) })
}, 'twilio-communications');
// Created here so the getFirestore(app, 'wix-chat') calls elsewhere (conversations and their messages) get this
// persistent-cache instance — it must be initialised before anything else opens that database.
export const dbWixChat = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentSingleTabManager(undefined) })
}, 'wix-chat');
for (const messageDb of [dbTwilio, dbWixChat]) {
  const indexManager = getPersistentCacheIndexManager(messageDb);
  if (indexManager) enablePersistentCacheIndexAutoCreation(indexManager);
}
// initializeAuth() with persistence specified upfront (vs getAuth() +
// setPersistence() afterward) avoids a redundant second async round-trip to
// IndexedDB to switch persistence after auth has already initialized with
// the default — that extra round-trip was adding measurable latency before
// onAuthStateChanged's first callback fires. indexedDBLocalPersistence tried
// first (more reliable in WebViews), falling back to browserLocalPersistence.
export const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence],
});
export const analytics = getAnalytics(app);
export const installations = getInstallations(app);

// Initialize Firebase Messaging - check for support first
let messaging: ReturnType<typeof getMessaging> | null = null;

export async function initializeMessaging() {
  if (messaging) return messaging;
  
  try {
    const supported = await isSupported();
    if (!supported) {
      console.warn('🔔 Firebase Cloud Messaging is not supported in this browser');
      return null;
    }
    
    messaging = getMessaging(app);
    console.log('✅ Firebase Cloud Messaging initialized');
    return messaging;
  } catch (error) {
    console.error('❌ Error initializing Firebase Cloud Messaging:', error);
    return null;
  }
}

// Try to initialize immediately
initializeMessaging().catch(error => {
  console.error('Failed to initialize messaging on app startup:', error);
});

export { app };

// Export a getter for messaging that ensures it's initialized
export function getMessagingInstance() {
  return messaging;
}

